/**
 * Crisis detection utility (P0).
 *
 * Detects suicide / self-harm / extreme crisis expressions in user input
 * and triggers a forced redirect to the dedicated crisis resources page,
 * bypassing all normal AI conversation, recommendation, and stage flows.
 *
 * Used by both the Homepage AI (WellnessConcierge) and the Dashboard AI
 * (AINavigatorChat) so behavior is identical pre- and post-login.
 */

// English crisis keywords / phrases (lowercased; matched as substrings or word patterns)
const CRISIS_PATTERNS_EN: RegExp[] = [
  /\bsuicide\b/i,
  /\bsuicidal\b/i,
  /\bkill (?:myself|me)\b/i,
  /\bend my life\b/i,
  /\bend it all\b/i,
  /\b(?:wanna|want to|wish to|going to) die\b/i,
  /\bi (?:want|wanna) to die\b/i,
  /\bdon'?t want to (?:live|be alive)\b/i,
  /\bno reason to live\b/i,
  /\bbetter off dead\b/i,
  /\btake my own life\b/i,
  /\boverdose\b/i,
  /\bself[-\s]?harm\b/i,
  /\bhurt myself\b/i,
  /\bcut(?:ting)? myself\b/i,
  /\bcan'?t go on\b/i,
];

// Chinese crisis keywords (Simplified + common Traditional variants)
// MIRROR of supabase/functions/_shared/crisis-lexicon.ts — keep these a UNION.
const CRISIS_PATTERNS_ZH: string[] = [
  '自杀', '自殺',
  '想死', '不想活', '不想活了', '活不下去',
  '结束生命', '結束生命', '结束自己的生命',
  '了结自己', '了結自己',
  '自残', '自殘', '伤害自己', '傷害自己',
  '割腕', '吞药', '吞藥', '过量服药', '過量服藥',
  '吃药自杀', '吃藥自殺',
  '跳楼', '跳樓',
  '没意义活着', '沒意義活著',
  '想消失',
];


/**
 * Returns true if the given text contains a crisis expression.
 */
export function detectCrisis(text: string | undefined | null): boolean {
  if (!text) return false;
  const trimmed = text.trim();
  if (!trimmed) return false;

  for (const re of CRISIS_PATTERNS_EN) {
    if (re.test(trimmed)) return true;
  }
  for (const phrase of CRISIS_PATTERNS_ZH) {
    if (trimmed.includes(phrase)) return true;
  }
  return false;
}

/**
 * Trigger a forced redirect to the in-app /crisis-resources page.
 * Adds a short delay so the user can see the inline notice toast first.
 *
 * - Sets sessionStorage flag `crisis_override_active` so the
 *   /crisis-resources page renders the immediate-support-only view.
 * - Uses window.location.assign for a hard navigation that breaks out of
 *   any in-flight AI streaming / recommendation flow.
 */
export function forceCrisisRedirect(opts?: { delayMs?: number; path?: string }) {
  const delay = opts?.delayMs ?? 800;
  const path = opts?.path ?? '/crisis-resources';
  try {
    sessionStorage.setItem('crisis_override_active', 'true');
  } catch {
    // ignore storage errors (private mode, etc.)
  }
  setTimeout(() => {
    window.location.assign(path);
  }, delay);
}
